/**
 * IncomeStatementTable — renders the income statement matching Fortnox layout:
 *
 * Two non-expandable main headers:
 *   RÖRELSENS INTÄKTER — contains revenue subsections
 *   RÖRELSENS KOSTNADER — contains cost subsections + financial + dispositions + tax
 *
 * Subsections under these are expandable (accounts visible when expanded).
 * K2 subtotals (RÖRELSERESULTAT, etc.) appear inline within RÖRELSENS KOSTNADER.
 * BERÄKNAT RESULTAT at the bottom (should be 0).
 */

import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
    formatAmount,
    formatAmountShowZero,
    yearHasData,
} from './resultReportUtils';

/**
 * Layout definition: maps backend sections into the two main display groups.
 * Each entry defines which backend section + subsection to render, and
 * which K2/working subtotals to insert after it.
 */
const REVENUE_ITEMS = [
    { type: 'subsection', backendSection: 'Rörelseintäkter, lagerförändringar m.m.', subsection: 'Nettoomsättning' },
    { type: 'subsection', backendSection: 'Rörelseintäkter, lagerförändringar m.m.', subsection: 'Förändring av lager av produkter i arbete, färdiga varor och pågående arbete för annans räkning' },
    { type: 'subsection', backendSection: 'Rörelseintäkter, lagerförändringar m.m.', subsection: 'Aktiverat arbete för egen räkning' },
    { type: 'subsection', backendSection: 'Rörelseintäkter, lagerförändringar m.m.', subsection: 'Övriga rörelseintäkter' },
];

const COST_ITEMS = [
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Råvaror och förnödenheter' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Handelsvaror' },
    { type: 'working-subtotal', key: 'bruttovinst', label: 'BRUTTOVINST' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Övriga externa kostnader' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Personalkostnader' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Av- och nedskrivningar av materiella och immateriella anläggningstillgångar' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Nedskrivningar av omsättningstillgångar utöver normala nedskrivningar' },
    { type: 'subsection', backendSection: 'Rörelsekostnader', subsection: 'Övriga rörelsekostnader' },
    { type: 'k2-subtotal', key: 'rorelseresultat', label: 'RÖRELSERESULTAT' },
    { type: 'subsection', backendSection: 'Finansiella poster', subsection: null }, // Render entire section as one expandable
    { type: 'k2-subtotal', key: 'resultat_efter_finansiella_poster', label: 'RESULTAT EFTER FINANSIELLA POSTER' },
    { type: 'subsection', backendSection: 'Bokslutsdispositioner', subsection: null },
    { type: 'k2-subtotal', key: 'resultat_fore_skatt', label: 'RESULTAT FÖRE SKATT' },
    { type: 'subsection', backendSection: 'Skatter', subsection: null },
];

export default function IncomeStatementTable({
    processedData,
    subtotals,
    showVerifications,
    compactMode,
    onVerificationClick,
}) {
    const [expandedSections, setExpandedSections] = useState({});
    const [expandedAccounts, setExpandedAccounts] = useState({});

    if (!processedData) return null;

    const availableYears = processedData.years;
    const displayYear = availableYears.find(y => yearHasData(processedData.data[y])) || availableYears[0];
    const currentYearData = processedData.data[displayYear];

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };
    const toggleAccount = (id) => {
        setExpandedAccounts(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // -----------------------------------------------------------------------
    // Get amount for a specific account across years
    // -----------------------------------------------------------------------
    function getAccountAmount(year, backendSection, subsectionKey, accountId, accountNumber) {
        const yearData = processedData?.data[year];
        if (!yearData) return 0;

        let section;
        if (subsectionKey) {
            section = yearData[backendSection]?.[subsectionKey];
        } else {
            section = yearData[backendSection];
        }
        if (!section) return 0;

        // Search accounts in this section and nested subsections
        const findAccount = (obj) => {
            if (!obj) return null;
            if (obj.accounts && Array.isArray(obj.accounts)) {
                const found = obj.accounts.find(a => a.account_id === accountId || a.account_number === accountNumber);
                if (found) return found;
            }
            for (const [k, v] of Object.entries(obj)) {
                if (k === 'accounts' || k === 'total_change' || k === 'accountRanges' || k === 'workingReportOnly' || typeof v !== 'object') continue;
                const found = findAccount(v);
                if (found) return found;
            }
            return null;
        };

        const acc = findAccount(section);
        if (!acc) return 0;
        const val = parseFloat(String(acc.change || 0).replace(/,/g, ''));
        return isNaN(val) ? 0 : val;
    }

    // -----------------------------------------------------------------------
    // Get section total across years
    // -----------------------------------------------------------------------
    function getSectionTotal(year, backendSection, subsectionKey) {
        const yearData = processedData?.data[year];
        if (!yearData) return 0;
        if (subsectionKey) {
            return yearData[backendSection]?.[subsectionKey]?.total_change || 0;
        }
        return yearData[backendSection]?.total_change || 0;
    }

    // -----------------------------------------------------------------------
    // Collect all accounts from a section (flat list)
    // -----------------------------------------------------------------------
    function collectAccounts(sectionData) {
        if (!sectionData) return [];
        const accounts = [];
        const collect = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            if (obj.accounts && Array.isArray(obj.accounts)) {
                accounts.push(...obj.accounts);
            }
            for (const [k, v] of Object.entries(obj)) {
                if (k === 'accounts' || k === 'total_change' || k === 'accountRanges' || k === 'workingReportOnly' || typeof v !== 'object') continue;
                collect(v);
            }
        };
        collect(sectionData);
        return accounts;
    }

    // -----------------------------------------------------------------------
    // Render an expandable subsection with accounts
    // -----------------------------------------------------------------------
    function renderSubsection(item) {
        const { backendSection, subsection: subsectionKey } = item;
        const sectionData = subsectionKey
            ? currentYearData?.[backendSection]?.[subsectionKey]
            : currentYearData?.[backendSection];

        if (!sectionData) return null;

        // Check if section has any activity
        const total = parseFloat(sectionData.total_change || 0);
        const accounts = collectAccounts(sectionData);
        const activeAccounts = accounts.filter(a => {
            const change = parseFloat(String(a.change || 0).replace(/,/g, ''));
            return Math.abs(change) > 0.005;
        });
        if (Math.abs(total) < 0.005 && activeAccounts.length === 0) return null;

        const displayName = subsectionKey || backendSection;
        const sectionKey = `${backendSection}.${subsectionKey || '_all'}`;
        const isExpanded = expandedSections[sectionKey] !== undefined ? expandedSections[sectionKey] : true;

        const rows = [];

        // Subsection header (expandable)
        rows.push(
            <tr
                key={`hdr-${sectionKey}`}
                className="cursor-pointer"
                onClick={() => toggleSection(sectionKey)}
            >
                <td style={{ paddingLeft: '1rem', borderBottom: 'none', paddingTop: '1.5rem' }}>
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronDown : faChevronRight}
                        size="xs"
                        style={{ color: '#999', marginRight: '0.5rem', width: '12px' }}
                    />
                    <strong>{displayName}</strong>
                </td>
                {availableYears.map(year => (
                    <td key={`${sectionKey}-${year}`} style={{ borderBottom: 'none' }}></td>
                ))}
            </tr>
        );

        // Accounts (when expanded and not compact)
        if (isExpanded && !compactMode) {
            activeAccounts.forEach(account => {
                const hasVer = showVerifications && account.verifications && account.verifications.length > 0;
                const isAccExpanded = expandedAccounts[account.account_id];

                rows.push(
                    <tr key={`acc-${account.account_id}`} className={hasVer ? 'cursor-pointer' : ''}>
                        <td style={{ paddingLeft: '3rem' }}>
                            <div className="account-info" onClick={() => hasVer && toggleAccount(account.account_id)}>
                                {hasVer && (
                                    <span style={{ color: '#666', width: '16px', marginRight: '0.25rem' }}>
                                        <FontAwesomeIcon icon={isAccExpanded ? faChevronDown : faChevronRight} size="xs" />
                                    </span>
                                )}
                                <span className="account-number">{account.account_number}</span>
                                <span className="account-name">{account.account_name}</span>
                                {hasVer && <span className="verification-count">({account.verifications.length})</span>}
                            </div>
                        </td>
                        {availableYears.map(year => (
                            <td key={`${account.account_id}-${year}`}>
                                {formatAmountShowZero(getAccountAmount(year, backendSection, subsectionKey, account.account_id, account.account_number))}
                            </td>
                        ))}
                    </tr>
                );

                // Verification rows
                if (hasVer && isAccExpanded) {
                    account.verifications.forEach((ver, idx) => {
                        rows.push(
                            <tr key={`ver-${account.account_id}-${idx}`} className="verification-row">
                                <td style={{ paddingLeft: '4rem' }}>
                                    <div className="verification-info">
                                        <small className="verification-details">
                                            <span className="verification-number" onClick={() => onVerificationClick && onVerificationClick(ver)}>
                                                {ver.verification_number}
                                            </span>
                                            <span className="verification-date">{new Date(ver.date).toLocaleDateString('sv-SE')}</span>
                                            <span className="verification-description">{ver.description}</span>
                                        </small>
                                    </div>
                                </td>
                                {availableYears.map((year, yi) => (
                                    <td key={`ver-${ver.verification_id}-${year}-${yi}`}>
                                        {yi === 0 ? (
                                            <small style={{ color: '#666' }}>
                                                {formatAmount(ver.transaction_type === 'credit' ? ver.amount : -ver.amount)}
                                            </small>
                                        ) : ''}
                                    </td>
                                ))}
                            </tr>
                        );
                    });
                }
            });
        }

        // Summa row
        rows.push(
            <tr key={`sum-${sectionKey}`} style={{ fontWeight: '600' }}>
                <td style={{ paddingLeft: '1rem', borderBottom: 'none', paddingBottom: '1.75rem' }}>Summa {displayName.toLowerCase()}</td>
                {availableYears.map(year => (
                    <td key={`sum-${sectionKey}-${year}`} style={{ borderBottom: 'none', paddingBottom: '1.75rem' }}>
                        {formatAmountShowZero(getSectionTotal(year, backendSection, subsectionKey))}
                    </td>
                ))}
            </tr>
        );

        return rows;
    }

    // -----------------------------------------------------------------------
    // Render a K2 subtotal row
    // -----------------------------------------------------------------------
    function renderK2Subtotal(item) {
        return (
            <tr key={`k2-${item.key}`} className="subtotal-row">
                <td>
                    <strong>{item.label}</strong>
                </td>
                {availableYears.map(year => {
                    const yearSub = subtotals[year];
                    const value = yearSub?.[item.key] || 0;
                    return (
                        <td key={`k2-${item.key}-${year}`} style={{
                            fontWeight: 'bold',
                            color: parseFloat(value) >= 0 ? '#198754' : '#dc3545',
                        }}>
                            {formatAmountShowZero(value)}
                        </td>
                    );
                })}
            </tr>
        );
    }

    // -----------------------------------------------------------------------
    // Render a working subtotal row (non-K2, lighter style)
    // -----------------------------------------------------------------------
    function renderWorkingSubtotal(item) {
        if (compactMode) return null;

        // Compute bruttovinst: revenue + råvaror + handelsvaror
        const computeValue = (year) => {
            const yearData = processedData?.data[year];
            if (!yearData) return 0;
            const revenue = parseFloat(yearData['Rörelseintäkter, lagerförändringar m.m.']?.total_change || 0);
            const ravaror = parseFloat(yearData['Rörelsekostnader']?.['Råvaror och förnödenheter']?.total_change || 0);
            const handelsvaror = parseFloat(yearData['Rörelsekostnader']?.['Handelsvaror']?.total_change || 0);
            return revenue + ravaror + handelsvaror;
        };

        return (
            <tr key={`working-${item.key}`}>
                <td style={{ borderTop: '1px dashed #ccc', borderBottom: '1px dashed #ccc', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                    <em style={{ fontSize: '0.9em', color: '#495057' }}>{item.label}</em>
                </td>
                {availableYears.map(year => (
                    <td key={`working-${item.key}-${year}`} style={{
                        borderTop: '1px dashed #ccc',
                        borderBottom: '1px dashed #ccc',
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        fontStyle: 'italic',
                        fontSize: '0.9em',
                        color: '#495057',
                    }}>
                        {formatAmountShowZero(computeValue(year))}
                    </td>
                ))}
            </tr>
        );
    }

    // -----------------------------------------------------------------------
    // Render items list (subsections + subtotals)
    // -----------------------------------------------------------------------
    function renderItems(items) {
        const rows = [];
        items.forEach(item => {
            if (item.type === 'subsection') {
                const result = renderSubsection(item);
                if (result) rows.push(...result);
            } else if (item.type === 'k2-subtotal') {
                rows.push(renderK2Subtotal(item));
            } else if (item.type === 'working-subtotal') {
                const result = renderWorkingSubtotal(item);
                if (result) rows.push(result);
            }
        });
        return rows;
    }

    // -----------------------------------------------------------------------
    // Compute main section total
    // -----------------------------------------------------------------------
    function getMainTotal(year, items) {
        let total = 0;
        items.forEach(item => {
            if (item.type === 'subsection') {
                total += parseFloat(getSectionTotal(year, item.backendSection, item.subsection)) || 0;
            }
        });
        return total;
    }

    // -----------------------------------------------------------------------
    // Compute Årets resultat (all sections sum)
    // -----------------------------------------------------------------------
    function getAresResultat(year) {
        const yearSub = subtotals[year];
        return yearSub?.arets_resultat || 0;
    }

    // -----------------------------------------------------------------------
    // Main render
    // -----------------------------------------------------------------------
    return (
        <div style={{ overflowX: 'auto' }}>
            <Table className="mb-0">
                <colgroup>
                    <col style={{ width: `${Math.max(40, 70 - (availableYears.length - 1) * 10)}%` }} />
                    {availableYears.map(year => (
                        <col key={year} style={{ width: `${60 / availableYears.length}%` }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        <th></th>
                        {availableYears.map(year => (
                            <th key={year} style={{ fontSize: '0.85em', fontWeight: 500 }}>{year}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* RÖRELSENS INTÄKTER */}
                    <tr>
                        <td colSpan={1 + availableYears.length} style={{ borderBottom: 'none', paddingTop: '1rem' }}>
                            <strong>RÖRELSENS INTÄKTER</strong>
                        </td>
                    </tr>
                    {renderItems(REVENUE_ITEMS)}
                    <tr className="summa-row">
                        <td><strong>SUMMA RÖRELSENS INTÄKTER</strong></td>
                        {availableYears.map(year => (
                            <td key={`sum-rev-${year}`}>
                                <strong>{formatAmountShowZero(getMainTotal(year, REVENUE_ITEMS))}</strong>
                            </td>
                        ))}
                    </tr>

                    {/* Spacer */}
                    <tr><td colSpan={1 + availableYears.length} style={{ height: '2rem', border: 'none', padding: 0 }}></td></tr>

                    {/* RÖRELSENS KOSTNADER */}
                    <tr>
                        <td colSpan={1 + availableYears.length} style={{ borderBottom: 'none', paddingTop: '1rem' }}>
                            <strong>RÖRELSENS KOSTNADER</strong>
                        </td>
                    </tr>
                    {renderItems(COST_ITEMS)}
                    <tr className="summa-row">
                        <td><strong>SUMMA RÖRELSENS KOSTNADER</strong></td>
                        {availableYears.map(year => (
                            <td key={`sum-cost-${year}`}>
                                <strong>{formatAmountShowZero(getMainTotal(year, COST_ITEMS))}</strong>
                            </td>
                        ))}
                    </tr>

                    {/* Spacer */}
                    <tr><td colSpan={1 + availableYears.length} style={{ height: '1rem', border: 'none', padding: 0 }}></td></tr>

                    {/* Årets resultat */}
                    <tr className="subtotal-row">
                        <td><strong>ÅRETS RESULTAT</strong></td>
                        {availableYears.map(year => {
                            const value = getAresResultat(year);
                            return (
                                <td key={`ar-${year}`} style={{
                                    fontWeight: 'bold',
                                    color: parseFloat(value) >= 0 ? '#198754' : '#dc3545',
                                }}>
                                    {formatAmountShowZero(value)}
                                </td>
                            );
                        })}
                    </tr>

                    {/* BERÄKNAT RESULTAT — should be 0 when all entries are balanced */}
                    <tr><td colSpan={1 + availableYears.length} style={{ height: '0.5rem', border: 'none', padding: 0 }}></td></tr>
                    <tr>
                        <td><strong>BERÄKNAT RESULTAT</strong></td>
                        {availableYears.map(year => {
                            // Compute: sum of all revenue + all costs (should net to 0 in a balanced system)
                            const revenueTotal = getMainTotal(year, REVENUE_ITEMS);
                            const costTotal = getMainTotal(year, COST_ITEMS);
                            const beraknat = revenueTotal + costTotal;
                            return (
                                <td key={`br-${year}`}>
                                    <strong style={{ color: Math.abs(beraknat) > 0.005 ? '#dc3545' : undefined }}>
                                        {formatAmountShowZero(beraknat)}
                                    </strong>
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </Table>
        </div>
    );
}
